import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { EHR_SYNC_REFRESH_GATED_MESSAGE, orgEhrUnusableMessage } from "@utils/ehrActions";
import { formatRelativeTime } from "@utils/formatRelativeTime";
import React, { useState } from "react";

import { Label, Tooltip } from "@fronterahealth/frontera-ui-components";

import { ApiLearnerManualGoalSyncStatusChoices, EhrConnectionStatus } from "@api/graphql/types-and-hooks";
import { EHRLoginDialog } from "@components/ClinicalNewReportDialog/EHRLoginDialog";
import { EhrSyncGatedNotice } from "@components/EHRConnect/EhrSyncGatedNotice";
import { useEhrAnalytics } from "@hooks/useEhrAnalytics";
import { useManualRefreshProgramming } from "@pages/ClinicalReportDetails/ReportPage/hooks/useManualRefreshProgramming";

export interface GoalsSyncButtonProps {}

export const GoalsSyncButton: React.FC<GoalsSyncButtonProps> = ({}) => {
  const {
    canRefresh,
    syncStatus,
    lastUpdated,
    isSyncing,
    refresh,
    orgEhrUnusable,
    ehrConnectionStatus,
    noCredentials,
    isEhrSyncCleared,
  } = useManualRefreshProgramming();
  const { captureEhrRefreshClicked } = useEhrAnalytics();
  const [ehrLoginDialogOpen, setEhrLoginDialogOpen] = useState(false);
  if (!canRefresh) return null;

  const labelText = () => {
    if (isSyncing) return "Refreshing data from EHR";
    if (orgEhrUnusable) {
      return ehrConnectionStatus === EhrConnectionStatus.CredentialsFailed
        ? "EHR credentials invalid"
        : "EHR connection unavailable";
    }
    if (noCredentials) return "EHR disconnected";
    if (syncStatus === ApiLearnerManualGoalSyncStatusChoices.Failure) return "Sync failed";
    if (!lastUpdated) return "";
    return `Last refreshed ${formatRelativeTime(new Date(lastUpdated))}`;
  };

  const content = (
    <div className="flex items-center">
      <button
        type="button"
        className="rounded-sm p-1 disabled:opacity-50"
        onClick={() => {
          captureEhrRefreshClicked("report_goals_step");
          if (noCredentials) setEhrLoginDialogOpen(true);
          else refresh();
        }}
        disabled={isSyncing || !canRefresh || orgEhrUnusable || !isEhrSyncCleared}
      >
        <ArrowPathIcon className={`h-4 w-4 text-icon-secondary ${isSyncing ? "animate-spin" : ""}`} />
      </button>
      {/* Managed-onboarding gate: inline copy next to the disabled control
          (never tooltip-only — a disabled button emits no hover/focus events). */}
      {!isEhrSyncCleared ? (
        <EhrSyncGatedNotice message={EHR_SYNC_REFRESH_GATED_MESSAGE} />
      ) : (
        <Label size="extra-small" colorType="secondary">
          {labelText()}
        </Label>
      )}
    </div>
  );

  const loginDialog = ehrLoginDialogOpen ? (
    <EHRLoginDialog
      open={ehrLoginDialogOpen}
      setOpen={setEhrLoginDialogOpen}
      setSuccessLogin={(success) => {
        setEhrLoginDialogOpen(false);
        if (success) refresh();
      }}
    />
  ) : null;

  // A disabled button doesn't emit hover events, so the Tooltip wraps the whole
  // button + label to explain why the refresh is unavailable for an org-admin
  // EHR connection.
  if (orgEhrUnusable) {
    return (
      <>
        <Tooltip
          text={orgEhrUnusableMessage(ehrConnectionStatus)}
          placement="bottom"
          showInfoIcon={false}
          className="inline-flex"
        >
          {content}
        </Tooltip>
        {loginDialog}
      </>
    );
  }

  return (
    <>
      {content}
      {loginDialog}
    </>
  );
};
